import { Input } from '@/components/ui/input';
import Spinner from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { Building2Icon, ChevronDownIcon, HomeIcon, SearchIcon } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useTenant } from '../../api/tenant';
import { getTenantProjects } from '../../api/tenant-projects';
import { useUserEntityPermissions } from '../api/entity-access';
import type { EntityAccess, EntityAccessPerson } from '../types/types';
import {
  buildTreeNodes,
  filterTreeNodes,
  type EntityTreeNode,
  type PermissionsResponse,
} from '../utils/entity-tree';
import { PermissionSummary } from './permission-summary';

const route = getRouteApi('/_authenticated/_sidebar-layout/tenant/$tenantId/entity-access');

type EntityTreePanelProps = {
  selectedPerson: EntityAccessPerson;
  selectedEntity: EntityAccess | null;
  onSelectEntity: (entity: EntityAccess) => void;
  detailsPanel?: ReactNode;
};

export const EntityTreePanel = ({
  selectedPerson,
  selectedEntity,
  onSelectEntity,
  detailsPanel,
}: EntityTreePanelProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const { tenantId } = route.useParams();
  const { data: tenant } = useTenant({ id: tenantId });
  const { data, isLoading: isLoadingPermissions } = useUserEntityPermissions({
    tenantId,
    userId: selectedPerson.user.id,
  });
  const { data: projects, isLoading: isLoadingProjects } = useQuery({
    queryKey: ['tenants', tenantId, 'projects'],
    queryFn: () => getTenantProjects(tenantId),
    enabled: Boolean(tenantId),
  });

  const permissions = data as PermissionsResponse | undefined;

  const treeNodes = useMemo(() => {
    const nodes = buildTreeNodes({ permissions, tenant, projects });
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) return nodes;

    return filterTreeNodes(nodes, normalizedSearch);
  }, [permissions, projects, search, tenant]);

  if (isLoadingPermissions || isLoadingProjects) {
    return (
      <main
        aria-busy="true"
        aria-live="polite"
        className="flex h-40 items-center justify-center rounded-md border bg-white p-4"
      >
        <Spinner className="size-10 text-primary" />
      </main>
    );
  }
  const { firstName, lastName, email } = selectedPerson.user;

  const displayName = `${firstName ?? ''} ${lastName ?? ''}`.trim() || email;
  const initial = displayName.charAt(0).toUpperCase();
  return (
    <div className="min-w-0 space-y-4">
      <div className="flex items-center gap-4">
        <span
          className={
            'flex size-9 shrink-0 items-center justify-center rounded-full bg-primary-light/10 text-sm font-semibold text-primary'
          }
        >
          {initial}
        </span>
        <div className="min-w-0">
          <h2 className="truncate text-md font-semibold leading-6 text-primary-darker">
            {displayName}
          </h2>
          <p className="truncate text-sm font-semibold leading-6 text-text-secondary">{email}</p>
        </div>
      </div>
      <div className="min-w-0">
        <Input
          size="sm"
          placeholder={t('search-entities-to-grant-access')}
          wrapperClassName="min-w-0"
          className="h-9 rounded-sm border-background-darker text-md font-normal leading-6 placeholder:text-text-secondary focus-visible:border-2 focus-visible:border-primary-light focus-visible:ring-primary-light/20"
          startIcon={<SearchIcon aria-hidden="true" className="size-4 text-text-secondary" />}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      <div
        className={cn(
          'grid min-w-0 gap-4',
          detailsPanel ? 'grid-cols-[minmax(0,1fr)_360px]' : 'grid-cols-[minmax(0,1fr)]'
        )}
      >
        <main className="min-w-0 overflow-hidden rounded-md border border-background-darker/60 bg-white">
          {treeNodes.map((node) => (
            <EntityTreeRow
              key={`${node.type}-${node.id}`}
              node={node}
              level={0}
              selectedEntity={selectedEntity}
              onSelectEntity={onSelectEntity}
            />
          ))}
        </main>
        {detailsPanel}
      </div>
    </div>
  );
};

type EntityTreeRowProps = {
  node: EntityTreeNode;
  level: number;
  selectedEntity: EntityAccess | null;
  onSelectEntity: (entity: EntityAccess) => void;
};

const EntityTreeRow = ({ node, level, selectedEntity, onSelectEntity }: EntityTreeRowProps) => {
  const [expanded, setExpanded] = useState(true);
  const selected = selectedEntity?.id === node.id && selectedEntity?.type === node.type;
  const hasChildren = Boolean(node.children?.length);
  const canManageAccess = node.type === 'project' || node.type === 'estate';
  const connectorLeft = 26 + (level - 1) * 28;

  return (
    <>
      <div className="relative">
        {level > 0 ? (
          <>
            <span
              className="absolute bottom-0 top-0 w-px bg-background-darker/50"
              style={{ left: connectorLeft }}
            />
            <span
              className="absolute h-px w-4 bg-background-darker/50"
              style={{ left: connectorLeft, top: 24 }}
            />
          </>
        ) : null}

        {selected ? (
          <span
            className="absolute bottom-0 top-0 w-1 bg-primary-light"
            style={{ left: 16 + level * 28 }}
          />
        ) : null}

        <div
          className={cn(
            'group flex h-12 w-full items-center justify-between border-b border-background-darker/30 pr-4 text-left transition-colors last:border-b-0 hover:bg-background-lighter/40',
            selected && 'bg-primary-light/10 text-primary'
          )}
          style={{ paddingLeft: 16 + level * 28 }}
        >
          <span className="flex min-w-0 flex-1 items-stretch gap-2 self-stretch @container">
            {hasChildren ? (
              <button
                type="button"
                aria-expanded={expanded}
                aria-label={`${expanded ? 'Collapse' : 'Expand'} ${node.name}`}
                className="flex w-4 shrink-0 items-center justify-center"
                onClick={() => setExpanded((current) => !current)}
              >
                <ChevronDownIcon
                  aria-hidden="true"
                  className={cn(
                    'size-4 text-text-secondary transition-transform',
                    !expanded && '-rotate-90'
                  )}
                />
              </button>
            ) : (
              <span className="size-4 shrink-0" />
            )}
            <button
              type="button"
              title={node.name}
              aria-label={`Select ${node.type} ${node.name}`}
              aria-current={selected ? 'true' : undefined}
              disabled={!canManageAccess}
              onClick={() => {
                if (canManageAccess) {
                  onSelectEntity(node);
                }
              }}
              className={cn(
                'flex min-w-0 flex-1 cursor-pointer items-center justify-between gap-3 text-left',
                !canManageAccess && 'cursor-default'
              )}
            >
              <span className="flex min-w-0 items-center gap-2">
                <EntityIcon type={node.type} selected={selected} />
                <span
                  className={cn(
                    'truncate text-md font-semibold',
                    selected ? 'text-primary-light' : 'text-primary-darker'
                  )}
                >
                  {node.name}
                </span>
              </span>

              <span className="flex shrink-0 items-center gap-4">
                <span className="hidden text-sm font-semibold capitalize text-text-secondary group-hover:inline">
                  {node.type}
                </span>
                <PermissionSummary access={node.access} permissions={node.permissions} />
              </span>
            </button>
          </span>
        </div>
      </div>

      {expanded
        ? node.children?.map((child) => (
            <EntityTreeRow
              key={`${child.type}-${child.id}`}
              node={child}
              level={level + 1}
              selectedEntity={selectedEntity}
              onSelectEntity={onSelectEntity}
            />
          ))
        : null}
    </>
  );
};

const EntityIcon = ({ type, selected }: { type: EntityTreeNode['type']; selected: boolean }) => {
  const className = cn('size-4 shrink-0', selected ? 'text-primary-light' : 'text-text-secondary');

  if (type === 'estate') return <HomeIcon aria-hidden="true" className={className} />;

  return <Building2Icon aria-hidden="true" className={className} />;
};
